<script lang="ts">
    import verified_checkmark from "$lib/assets/Verified.svg";

    import { popup } from "@skeletonlabs/skeleton";

    import he from "he";

    import { onMount } from "svelte";

    export let user_id: string | undefined;
    export let session_id: string | undefined;
    export let grantId: string;
    export let clickable: boolean = true;
    export let doLoad: boolean = true;
    export let grantTitle: string = "";
    export let agency: string = "";
    export let branch: string = "";
    export let dueDate: string | undefined = undefined;
    export let awardMoney: string | undefined = undefined;
    export let isPaid: boolean = false;
    export let phase: string = "";
    export let displaySummary: boolean = false;
    export let summary: string | undefined = undefined;
    export let verified: boolean = false;

    async function cardListener(event: Event) {
        if (clickable) {
            window.open(`/search/result/${grantId}`, "_blank");
        }
    }

    onMount(() => {
        if (doLoad) {
            fetch(`/api/grantrec/log`, {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    user_id: user_id,
                    session_id: session_id,
                    query: grantId,
                    action: "results",
                }),
            });
            fetch(
                `/api/grantrec/find_grant?` + new URLSearchParams({ type: "id", query: grantId }),
            ).then((resp: Response) => {
                resp.json().then((resp) => {
                    const result = resp.results;

                    grantTitle = result.Title;
                    agency = result.Agency;
                    branch = result.Branch ?? "";
                    awardMoney = result.AwardCeiling;
                    phase = result.Phase;
                    summary = result.SummaryShort;
                    verified = result.Verified;
                    dueDate = result.CloseDate;
                });
            });
        }
    });
</script>

<div class="col-start-1 col-end-6 grid grid-cols-[min-content_1fr] gap-2 w-full">
    <div class="grid grid-cols-subgrid col-span-7 items-center">
        <div
            class="grid grid-cols-subgrid col-span-7 col-start-1 row-start-1 card bg-secondary-500 rounded-none sm:rounded-lg transition duration-100"
            class:hover:shadow-md={clickable}
            class:hover:cursor-pointer={clickable}
            on:click={cardListener}
            id="bookmark_item_{grantId}"
        >
            <div class="grid grid-cols-subgrid col-span-6 xs:px-3 px-5 py-3 items-center">
                <div class="col-start-1 row-start-1 row-span-2">
                    <slot name="lead" />
                </div>

                <div class="col-start-2 row-start-1 xs:col-span-4 whitespace-pre-line">
                    <p class="text-base text-left">
                        <b class="relative">
                            {he.decode(grantTitle)}
                            {#if verified}
                                <img
                                    src={verified_checkmark}
                                    alt="Verified Grant"
                                    class="ml-1 w-5 h-5 absolute left-full bottom-0.5"
                                    use:popup={{ event: "hover", target: "verified_info", placement: "top" }}
                                />
                            {/if}
                        </b>
                    </p>
                </div>

                <div
                    class="mt-2 xs:gap-1 gap-2 grid sm:grid-flow-col xs:grid-cols-subgrid xs:col-span-2 row-start-2 sm:justify-start items-center text-center"
                >
                    <span class="bg-secondary-600 py-1 px-2 rounded-lg text-xs">{grantId}</span>
                    <span class="sm:hidden bg-blue-600 px-2 py-0.5 rounded-lg text-white whitespace-nowrap text-sm"
                        >{dueDate
                            ? new Date(dueDate) < new Date("01/01/9998")
                                ? new Date(dueDate).toLocaleDateString("en-US", {
                                      year: "numeric",
                                      month: "long",
                                      day: "numeric",
                                      timeZone: "UTC",
                                  })
                                : "No Due Date"
                            : "See Topic Link"}</span
                    >
                    {#if phase}
                        {#each phase.split("+") as p}
                            <span class="bg-blue-600 bg-opacity-20 py-1 px-2 rounded-lg text-xs">{p}</span>
                        {/each}
                    {/if}
                    <span class="sm:hidden bg-yellow-300 px-2 py-0.5 rounded-lg whitespace-nowrap text-sm"
                        >{awardMoney
                            ? new Intl.NumberFormat("en-US", {
                                  style: "currency",
                                  currency: "USD",
                                  maximumFractionDigits: 0,
                                  minimumFractionDigits: 0,
                              }).format(awardMoney)
                            : "See Topic Link"}</span
                    >
                    <span class="text-sm py-1 sm:text-left xs:col-span-2"
                        >{agency}{branch ? " - " + (branch.length > 100 ? "Multiple Branches" : branch) : ""}</span
                    >
                </div>

                <div
                    class="xs:hidden gap-4 col-start-3 row-start-1 xs:row-start-2 row-span-2 xs:row-span-1 text-center"
                >
                    <span class="bg-blue-600 px-2 py-0.5 rounded-lg text-white whitespace-nowrap text-sm"
                        >{dueDate
                            ? new Date(dueDate) < new Date("01/01/9998")
                                ? new Date(dueDate).toLocaleDateString("en-US", {
                                      year: "numeric",
                                      month: "long",
                                      day: "numeric",
                                      timeZone: "UTC",
                                  })
                                : "No Due Date"
                            : "See Topic Link"}</span
                    >
                </div>
                <d2
                    class="xs:hidden gap-4 col-start-4 xs:col-start-3 row-start-1 xs:row-start-2 row-span-2 xs:row-span-1 text-center"
                >
                    <span class="bg-yellow-300 px-2 py-0.5 rounded-lg whitespace-nowrap text-sm"
                        >{awardMoney
                            ? new Intl.NumberFormat("en-US", {
                                  style: "currency",
                                  currency: "USD",
                                  maximumFractionDigits: 0,
                                  minimumFractionDigits: 0,
                              }).format(awardMoney)
                            : "See Topic Link"}</span
                    >
                </d2>
                {#if isPaid}
                    <div class="xs:hidden gap-4 col-start-5 row-start-1 row-span-2">
                        <span class="variant-filled-success px-2 py-0.5 rounded-lg whitespace-nowrap text-sm">Paid</span
                        >
                    </div>
                {/if}

                {#if displaySummary}
                    <div class="col-start-1 row-start-3 col-span-6 pt-3">
                        <hr class="!border-stone-400 h-0.5 pb-3" />
                        <p>{summary ?? ""}</p>
                    </div>
                {/if}
                <div class="xs:col-start-1 xs:col-span-6 col-start-7 xs:row-start-3 row-start-1 sm:row-span-2">
                    <slot name="trail" />
                </div>
                <div class="bg-black rounded-lg p-3 transition duration-100" data-popup="verified_info">
                    <p class="text-xs text-white">
                        Our team has reviewed and confirmed the accuracy of this solicitation.
                    </p>
                    <div class="arrow bg-black" />
                </div>
            </div>
        </div>
    </div>
</div>
